<template>
  <AppHead />
  <div class="flex min-h-screen flex-col">
    <TheHeader />
    <main>
      <App404 v-if="$route.meta.notFound" />
      <RouterView v-else />

      <AppSeoRenderedSection />
    </main>
    <TheFooter />

    <ClientOnly>
      <Teleport to="#modals">
        <LoginAsideDialog />
        <SignupAsideDialog />
        <RestorePasswordAsideDialog />
        <AppMobileMenu />
        <AppMobileAccountMenu />
      </Teleport>

      <AppToastContainer />
    </ClientOnly>

    <AppExtraScripts />
  </div>
</template>

<script lang="ts" setup>
import { nextTick, onServerPrefetch, useSSRContext, watch } from 'vue'
import { useRoute } from 'vue-router'

import {
  LoginAsideDialog,
  SignupAsideDialog,
  RestorePasswordAsideDialog,
} from '/@auth'

import {
  AppHead,
  AppExtraScripts,
  TheFooter,
  TheHeader,
  AppMobileMenu,
  AppMobileAccountMenu,
  App404,
  AppSeoRenderedSection,
} from '/@app'

import {
  type SSRContext,
  isClient,
  handleDeviceFlags,
  AppToastContainer,
} from '/@shared/app'

import { getCookieFromString } from '/@shared/utils'
import { useI18n } from './i18n'

import {
  type CurrencyCode,
  useAppStore,
  useAuthStore,
  useCurrencyStore,
  usePagesStore,
} from '/@/stores'

const authStore = useAuthStore()
const appStore = useAppStore()
const pagesStore = usePagesStore()
const currencyStore = useCurrencyStore()

const route = useRoute()

import { useLocaleFeatures } from '/@app'

const { locale } = useI18n()
const { isRuUser, isUaUser, currencyList } = useLocaleFeatures()

onServerPrefetch(async () => {
  const ssrCtx = useSSRContext<SSRContext>()

  appStore.entryHost = ssrCtx?.headers?.host
  appStore.entryProtocol = ssrCtx?.protocol

  const p: Promise<any>[] = [
    appStore.fetchCompanies(),
    pagesStore.fetchCurrentPageContext(),
  ]

  if (ssrCtx?.headers?.['cf-ipcountry']) {
    appStore.features = {
      country_code: ssrCtx.headers['cf-ipcountry'] as string,
    }
  }

  const currency = ssrCtx?.headers?.cookie
    ? getCookieFromString(ssrCtx.headers.cookie, 'fornex_currency')
    : undefined

  if (currency) currencyStore.currency = currency as CurrencyCode

  const authFlagCookie = ssrCtx?.headers?.cookie
    ? getCookieFromString(ssrCtx.headers.cookie, 'fornex_user')
    : undefined

  authStore.hasAuthCookie = Boolean(authFlagCookie)

  p.push(currencyStore.fetchList())

  await Promise.all(p)
})

if (isClient) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  document.cookie = `django_timezone=${timezone}; Path=/; SameSite=Lax`

  const localCurrency = localStorage.getItem(
    'app:currency'
  ) as CurrencyCode | null

  currencyStore.currency = localCurrency || 'EUR'

  authStore.getAuthStatus()

  if (!appStore.features) appStore.fetchFeatures()
  currencyStore.fetchList()
  appStore.fetchCompanies()

  const stopEntryPageHintWatcher = watch(
    route,
    (_, from) => {
      if (from) return

      nextTick(() => {
        appStore.isEntryPage = false

        stopEntryPageHintWatcher()
      })
    },
    { immediate: true }
  )

  watch(
    () => route.name,
    () => pagesStore.fetchCurrentPageContext()
  )
  watch(locale, () => pagesStore.fetchCurrentPageContext())

  const stopCurrencyWatcher = watch(
    [() => authStore.profile, () => appStore.features],
    val => {
      const [profile, feautres] = val

      if (!feautres) return
      if (profile === undefined) return

      const localCurrency = localStorage.getItem(
        'app:currency'
      ) as CurrencyCode | null

      const isSelectedCurrencyAvailable = Boolean(
        !localCurrency ||
          currencyList.value.find(x => x.short === localCurrency)
      )

      if (!isSelectedCurrencyAvailable) {
        currencyStore.updateCurrency('EUR')
        return
      }

      if (isRuUser.value) currencyStore.updateCurrency('RUR')
      else if (isUaUser.value) currencyStore.updateCurrency('UAH')
      else currencyStore.updateCurrency(localCurrency || 'EUR')

      nextTick(() => stopCurrencyWatcher())
    },
    {
      immediate: true,
    }
  )

  handleDeviceFlags()
}
</script>
